import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { CreateRoleDto, RoleDto, SiteDto } from '@services/api'
import { createRole, updateRole } from '@state/thunks/roleThunk'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { FC, useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { enqueueSnackbar } from 'notistack'

interface RoleFormProps {
  open: boolean
  setOpen: (open: boolean) => void
  sites: SiteDto[]
  editingRole: RoleDto | null
  setEditingRole: (role: RoleDto | null) => void
}

interface RoleFormInputs {
  name: string
  description?: string
  siteId: number
}

const RoleSchema = yup.object().shape({
  name: yup
    .string()
    .required('Role name is required')
    .max(50, 'Name cannot exceed 50 characters'),
  description: yup.string().optional().nullable(),
  siteId: yup
    .number()
    .typeError('You must select a site')
    .required('Site association is required'),
})

const RoleForm: FC<RoleFormProps> = ({
  open,
  setOpen,
  sites,
  editingRole,
  setEditingRole,
}) => {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (open) {
      if (editingRole === null) {
        roleReset({
          name: '',
          description: '',
          siteId: '' as unknown as number,
        })
      } else {
        roleReset({
          name: editingRole.name,
          description: editingRole.description,
          siteId: editingRole.siteId,
        })
      }
    }
  }, [open, editingRole, setEditingRole])

  const {
    handleSubmit: handleRoleSubmit,
    control: roleControl,
    reset: roleReset,
    formState: { errors, isSubmitting },
  } = useForm<RoleFormInputs>({
    resolver: yupResolver(RoleSchema),
    defaultValues: {
      name: '',
      description: '',
      siteId: '' as unknown as number,
    },
  })

  const onSubmit: SubmitHandler<RoleFormInputs> = async (data) => {
    if (!user) {
      return
    }

    try {
      const createRoleDto: CreateRoleDto = {
        name: data.name,
        description: data.description || '',
        siteId: data.siteId,
        members: [{ id: user.id }],
      }

      if (editingRole !== null) {
        await dispatch(
          updateRole({
            body: {
              ...data,
              permissions: editingRole.permissions.map((p) => p.permission),
            },
            id: editingRole.id,
          }),
        )
        enqueueSnackbar('Role updated successfully', { variant: 'success' })
      } else {
        await dispatch(createRole(createRoleDto))
        enqueueSnackbar('Role created successfully', { variant: 'success' })
      }

      handleClose()
    } catch (error) {
      console.error('Submission failed', error)
    }
  }

  const handleClose = () => {
    setEditingRole(null)
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      key={editingRole !== null ? `edit-${editingRole.id}` : 'create'}
    >
      <form onSubmit={handleRoleSubmit(onSubmit)} noValidate>
        <DialogTitle>
          {editingRole === null ? 'Créer un nouveau' : 'Modifier'} rôle
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}></DialogContentText>

          <Stack spacing={3} sx={{ mt: 1 }}>
            <Controller
              name="name"
              control={roleControl}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.name}
                  disabled={isSubmitting}
                >
                  <TextField
                    {...field}
                    label="Nom du Rôle"
                    variant="outlined"
                    error={!!errors.name}
                  />
                  <FormHelperText>{errors.name?.message}</FormHelperText>
                </FormControl>
              )}
            />
            <Controller
              name="description"
              control={roleControl}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.description}
                  disabled={isSubmitting}
                >
                  <TextField
                    {...field}
                    label="Description"
                    multiline
                    rows={3}
                    variant="outlined"
                    error={!!errors.description}
                  />
                  <FormHelperText>{errors.description?.message}</FormHelperText>
                </FormControl>
              )}
            />

            <Controller
              name="siteId"
              control={roleControl}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.siteId}
                  disabled={isSubmitting}
                >
                  <InputLabel id="site-select-label">Site *</InputLabel>
                  <Select
                    {...field}
                    labelId="site-select-label"
                    label="Site *"
                    value={field.value || ''}
                  >
                    {sites?.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.siteId?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {editingRole !== null ? 'Sauvegarder' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default RoleForm
